(() => {
  const initialReviewLimit = 6;
  const reviewIncrement = 3;
  const imageWidths = [640, 1280];

  const initialise = (root) => {
    if (root.dataset.pgHomeReviewsReady === 'true') return;
    root.dataset.pgHomeReviewsReady = 'true';

    let feedTimer = null;
    let alignmentTimer = null;
    let loadAttempts = 0;
    let visibleReviewLimit = initialReviewLimit;

    const provider = () => root.querySelector('.jdgm-all-reviews-widget-v2025');
    const reviewItems = () => Array.from(root.querySelectorAll('.jdgm-masonry-grid__masonry-item'));

    const expectedReviewCount = () => {
      const dataScript = root.querySelector('script.jdgm-all-reviews-widget-v2025-data-json');
      if (!dataScript) return 0;

      try {
        return Number(JSON.parse(dataScript.textContent || '{}').number_of_reviews || 0);
      } catch (error) {
        return 0;
      }
    };

    const reviewImageUrl = (source, width) => {
      if (!source || !source.includes('review-images.judgeme.com')) return source;

      try {
        const url = new URL(source, window.location.href);
        url.searchParams.set('quality', '90');
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (error) {
        return source;
      }
    };

    const upgradeReviewImages = () => {
      root.querySelectorAll('.jdgm-review-card__media-frame img').forEach((image) => {
        const source = image.getAttribute('src') || image.getAttribute('data-src') || '';
        if (!source.includes('review-images.judgeme.com')) return;

        const standardSource = reviewImageUrl(source, imageWidths[0]);
        const retinaSource = reviewImageUrl(source, imageWidths[1]);
        const sourceSet = standardSource + ' ' + imageWidths[0] + 'w, ' + retinaSource + ' ' + imageWidths[1] + 'w';
        const sizes = '(max-width: 749px) calc(100vw - 72px), (max-width: 989px) calc((100vw - 90px) / 2), calc((min(100vw, 1440px) - 128px) / 3)';

        image.setAttribute('src', standardSource);
        image.setAttribute('srcset', sourceSet);
        image.setAttribute('sizes', sizes);
        image.setAttribute('loading', 'lazy');
        image.setAttribute('decoding', 'async');

        if (image.hasAttribute('data-src')) image.setAttribute('data-src', standardSource);
        if (image.hasAttribute('data-srcset')) image.setAttribute('data-srcset', sourceSet);
      });
    };

    const ensureMoreButton = () => {
      const grid = root.querySelector('.jdgm-masonry-grid');
      if (!grid) return null;

      let button = root.querySelector('[data-pg-home-reviews-more]');
      if (button) return button;

      button = document.createElement('button');
      button.type = 'button';
      button.className = 'pg-home-reviews__more';
      button.dataset.pgHomeReviewsMore = 'true';
      button.setAttribute('aria-label', 'Show 3 more reviews');
      button.innerHTML = '<span data-pg-home-reviews-label>View more reviews</span><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4"></path></svg>';
      grid.insertAdjacentElement('afterend', button);

      button.addEventListener('click', () => {
        const availableReviewCount = reviewItems().length;
        visibleReviewLimit = Math.min(visibleReviewLimit + reviewIncrement, availableReviewCount);
        syncVisibility();
        scheduleAlignment();
      });

      return button;
    };

    const syncVisibility = () => {
      const items = reviewItems();
      const button = ensureMoreButton();
      if (!button) return;

      items.forEach((item, index) => {
        item.classList.toggle('pg-home-reviews__review-hidden', index >= visibleReviewLimit);
      });

      const remaining = Math.max(0, items.length - visibleReviewLimit);
      button.hidden = remaining === 0;
      button.setAttribute('aria-label', 'Show ' + Math.min(reviewIncrement, remaining) + ' more reviews');
      const label = button.querySelector('[data-pg-home-reviews-label]');
      if (label) label.textContent = 'View more reviews';
    };

    const visibleItems = () => reviewItems().filter((item) => (
      !item.classList.contains('pg-home-reviews__review-hidden') &&
      item.getClientRects().length > 0
    ));

    const alignRows = () => {
      const items = visibleItems();
      const stacks = items
        .map((item) => item.querySelector('.jdgm-review-card__content > .jm-stack'))
        .filter(Boolean);

      stacks.forEach((stack) => {
        Array.from(stack.children).slice(0, 3).forEach((zone) => {
          zone.style.removeProperty('min-height');
        });
      });

      if (window.matchMedia('(max-width: 749px)').matches) return;

      const rows = [];
      items.forEach((item) => {
        const top = item.getBoundingClientRect().top;
        let row = rows.find((candidate) => Math.abs(candidate.top - top) <= 2);
        if (!row) {
          row = { top, items: [] };
          rows.push(row);
        }
        row.items.push(item);
      });

      rows.forEach((row) => {
        const rowStacks = row.items
          .map((item) => item.querySelector('.jdgm-review-card__content > .jm-stack'))
          .filter(Boolean);

        [0, 1, 2].forEach((zoneIndex) => {
          const zones = rowStacks.map((stack) => stack.children[zoneIndex]).filter(Boolean);
          const height = Math.max(0, ...zones.map((zone) => Math.ceil(zone.getBoundingClientRect().height)));
          zones.forEach((zone) => zone.style.setProperty('min-height', height + 'px', 'important'));
        });
      });
    };

    const scheduleAlignment = () => {
      window.clearTimeout(alignmentTimer);
      alignmentTimer = window.setTimeout(() => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(alignRows));
      }, 80);
    };

    const completeFeed = () => {
      const widget = provider();
      if (!widget) return;

      root.classList.add('pg-home-reviews--controlled');
      upgradeReviewImages();
      syncVisibility();
      scheduleAlignment();

      const items = reviewItems();
      const expected = expectedReviewCount();
      const nativeButton = root.querySelector('.jdgm-masonry-grid__btn');
      const feedComplete = (expected && items.length >= expected) || (!nativeButton && items.length);

      if (feedComplete || !nativeButton || nativeButton.dataset.pgHomeAutoExpand === 'true' || loadAttempts >= 12) return;

      nativeButton.dataset.pgHomeAutoExpand = 'true';
      loadAttempts += 1;
      nativeButton.click();

      window.setTimeout(() => {
        if (nativeButton.isConnected) delete nativeButton.dataset.pgHomeAutoExpand;
        scheduleFeed();
      }, 900);
    };

    const scheduleFeed = () => {
      window.clearTimeout(feedTimer);
      feedTimer = window.setTimeout(completeFeed, 120);
    };

    new MutationObserver(() => {
      scheduleFeed();
      scheduleAlignment();
    }).observe(root, { childList: true, subtree: true });

    root.addEventListener('change', () => {
      loadAttempts = 0;
      visibleReviewLimit = initialReviewLimit;
      scheduleFeed();
      scheduleAlignment();
    });

    root.addEventListener('load', scheduleAlignment, true);
    window.addEventListener('resize', scheduleAlignment, { passive: true });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleAlignment);
    }

    scheduleFeed();
    scheduleAlignment();
  };

  const boot = () => document.querySelectorAll('.pg-home-reviews').forEach(initialise);
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
  boot();
})();
